/**
 * The app's data: one in-memory snapshot, mirrored to IndexedDB.
 *
 * OPTIMISTIC. Every mutation updates the snapshot and notifies subscribers synchronously, then
 * writes through to the backend in order. If a write fails, only the records that write
 * touched are put back (and only if nothing changed them since), and `onError` listeners hear
 * about it, so the shell can say so.
 *
 * STAMPED. Every user change stamps `updated_at` (monotonic, so two edits in one millisecond
 * still order) and marks the record dirty for sync. Remote records from sync are applied with
 * last-writer-wins and are not stamped or marked.
 *
 * The catalog is derived: bundled data overlaid by the user's foods, recipes and ratings
 * (store/overlay.ts), memoised on the identity of its inputs.
 */
import type {
  AvailabilityRating,
  AvailabilityTable,
  AvailabilityVote,
  BasketEntry,
  Catalog,
  Favourite,
  Food,
  Recipe,
  RegionCode,
  Section,
  SyncKind,
  SyncRecord,
  Trip,
} from '@resupport/core'
import {
  buildCatalog,
  canonicalJson,
  mergeCatalog,
  mergeTrips,
  newId,
  nowIso,
  restampTrip,
  slugify,
  timeOf,
  uniqueId,
} from '../engine'
import type { StoreBackend, Table } from './backend'
import { defaultBackend } from './backend'
import type { BundledData } from './bundled'
import { isFoodLike, isRecipeLike } from './bundled'
import {
  applyFoodRatings,
  communityTable,
  isNewer,
  isResetMarker,
  layerTables,
  stripUserAvailability,
  userTable,
} from './overlay'
import type { ResolvedSettings, Settings, SyncConsent } from './settings'
import { DEFAULT_SETTINGS, normaliseSettings, resolveSettings } from './settings'
import type { ExportFile, ImportSummary, ScratchBasket, SyncMeta, UserRating } from './types'
import { EMPTY_BASKET, EMPTY_SYNC, ratingKey, scratchGoalOf } from './types'

type RecordTable = 'trips' | 'foods' | 'recipes' | 'availability' | 'favourites'
type MetaKey = 'settings' | 'sync' | 'basket' | 'community'

export interface StoreState {
  ready: boolean
  /** False when the browser would not open IndexedDB: nothing survives closing the tab. */
  persistent: boolean
  error: Error | null
  trips: ReadonlyMap<string, Trip>
  /** The user layer only; the merged view is `store.catalog()`. */
  foods: ReadonlyMap<string, Food>
  recipes: ReadonlyMap<string, Recipe>
  ratings: ReadonlyMap<string, UserRating>
  /** Starred foods and recipes, by `food:<id>` / `recipe:<id>`; un-starred ones are tombstones. */
  favourites: ReadonlyMap<string, Favourite>
  /** Community ratings pulled by sync, category → region → rating. */
  community: AvailabilityTable
  settings: Settings
  sync: SyncMeta
  basket: ScratchBasket
}

/** Undo the change that returned this. Restores the earlier record with a fresh stamp. */
export type Undo = () => Promise<void>

/** Where a basket lives: a planned section, or the plan-free scratch basket. */
export type BasketTarget = { tripId: string; sectionId: string } | 'scratch'

export interface StoreOptions {
  backend?: StoreBackend
  bundled?: BundledData
  /** Injected for tests. Defaults to the engine's `nowIso`. */
  now?: () => string
  /** Default home region before the person picks one (from the browser locale). */
  localeRegion?: RegionCode
  /**
   * Asks the browser not to evict this origin's storage (`navigator.storage.persist()`).
   * Injected for tests; null never asks.
   */
  persistStorage?: (() => Promise<boolean>) | null
}

/** Set once `navigator.storage.persist()` has been asked, so it is asked once. */
const META_PERSIST_ASKED = 'persist_requested'

async function defaultPersistStorage(): Promise<boolean> {
  const storage = (
    globalThis as {
      navigator?: {
        storage?: { persist?: () => Promise<boolean>; persisted?: () => Promise<boolean> }
      }
    }
  ).navigator?.storage
  if (!storage?.persist) return false
  if (storage.persisted && (await storage.persisted())) return true
  return storage.persist()
}

export type NewSection = Partial<Omit<Section, 'id'>> & { name: string }

const FIELD = {
  trips: 'trips',
  foods: 'foods',
  recipes: 'recipes',
  availability: 'ratings',
  favourites: 'favourites',
} as const satisfies Record<RecordTable, keyof StoreState>

const KIND: Record<RecordTable, SyncKind> = {
  trips: 'trip',
  foods: 'food',
  recipes: 'recipe',
  availability: 'availability',
  favourites: 'favourite',
}

const TABLE_OF: Record<SyncKind, RecordTable> = {
  trip: 'trips',
  food: 'foods',
  recipe: 'recipes',
  availability: 'availability',
  favourite: 'favourites',
}

type Op =
  | { table: RecordTable; key: string; value: { id: string; updated_at?: string } }
  | { table: 'meta'; key: MetaKey; value: unknown }

const EMPTY_BUNDLED: BundledData = {
  categories: [],
  foods: [],
  recipes: [],
  availability: {},
  regions: [],
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

export function isTripLike(v: unknown): v is Trip {
  return (
    isObj(v) && typeof v.id === 'string' && typeof v.name === 'string' && Array.isArray(v.sections)
  )
}

const METHODS: readonly Section['method'][] = ['carry', 'food-drop', 'post', 'buy-in-town']
const STATUSES: readonly Section['status'][] = ['planned', 'shopping', 'shopped', 'walked']
const objects = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter(isObj) : [])
const finiteOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback

/**
 * A trip from outside this device (sync, an import file, an older client) made safe to plan
 * with: every section gets the arrays and slot counts the planner iterates, so one malformed
 * record cannot take down the Plan and Trip screens. Returns undefined when it is not a trip.
 */
export function normaliseTrip(v: unknown): Trip | undefined {
  if (!isTripLike(v)) return undefined
  const sections: Section[] = []
  for (const s of objects(v.sections)) {
    if (typeof s.id !== 'string' || !s.id) continue
    const slots = isObj(s.slots) ? s.slots : {}
    sections.push({
      ...s,
      id: s.id,
      name: typeof s.name === 'string' ? s.name : s.id,
      days: Math.max(0, finiteOr(s.days, 0)),
      method: METHODS.includes(s.method as Section['method'])
        ? (s.method as Section['method'])
        : 'buy-in-town',
      kcal_per_day: Math.max(0, finiteOr(s.kcal_per_day, 0)),
      slots: {
        breakfast: Math.max(0, finiteOr(slots.breakfast, 0)),
        lunch: Math.max(0, finiteOr(slots.lunch, 0)),
        dinner: Math.max(0, finiteOr(slots.dinner, 0)),
      },
      day_lines: objects(s.day_lines) as unknown as Section['day_lines'],
      meal_lines: objects(s.meal_lines) as unknown as Section['meal_lines'],
      status: STATUSES.includes(s.status as Section['status'])
        ? (s.status as Section['status'])
        : 'planned',
      basket: objects(s.basket) as unknown as Section['basket'],
    } as Section)
  }
  return { ...v, sections }
}

/** A soft delete as the worker sends it back: `{ id }` and the flag, nothing else. */
const isTombstone = (v: unknown): v is { id: string; deleted: true } =>
  isObj(v) && typeof v.id === 'string' && v.deleted === true

export function isRatingRecord(v: unknown): v is UserRating {
  return (
    isObj(v) &&
    typeof v.id === 'string' &&
    typeof v.category === 'string' &&
    typeof v.region === 'string' &&
    typeof v.level === 'number'
  )
}

export function isFavouriteRecord(v: unknown): v is Favourite {
  return (
    isObj(v) &&
    typeof v.id === 'string' &&
    (v.target === 'food' || v.target === 'recipe') &&
    typeof v.ref === 'string' &&
    v.id === `${v.target}:${v.ref}`
  )
}

/** "food:clif-bar". */
export function favouriteKey(target: Favourite['target'], ref: string): string {
  return `${target}:${ref}`
}

const GUARD: Record<RecordTable, (v: unknown) => boolean> = {
  trips: isTripLike,
  foods: isFoodLike,
  recipes: isRecipeLike,
  availability: isRatingRecord,
  favourites: isFavouriteRecord,
}

/** What may live in a table: its records, plus reset markers for foods and recipes. */
const STORABLE: Record<RecordTable, (v: unknown) => boolean> = {
  trips: isTripLike,
  foods: (v) => isFoodLike(v) || isResetMarker(v),
  recipes: (v) => isRecipeLike(v) || isResetMarker(v),
  availability: isRatingRecord,
  favourites: isFavouriteRecord,
}

export class Store {
  readonly bundled: BundledData
  private readonly backend: StoreBackend
  private readonly clock: () => string
  /** The region the browser locale suggests: what "Auto" means for the home region. */
  readonly localeRegion: RegionCode | undefined
  private state: StoreState
  private listeners = new Set<() => void>()
  private errorListeners = new Set<(err: Error) => void>()
  private queue: Promise<void> = Promise.resolve()
  private lastStamp = 0
  private loading: Promise<void> | null = null
  private readonly persistStorage: (() => Promise<boolean>) | null

  constructor(opts: StoreOptions = {}) {
    this.backend = opts.backend ?? defaultBackend()
    this.bundled = opts.bundled ?? EMPTY_BUNDLED
    this.clock = opts.now ?? nowIso
    this.localeRegion = opts.localeRegion
    this.persistStorage =
      opts.persistStorage === undefined ? defaultPersistStorage : opts.persistStorage
    this.state = {
      ready: false,
      persistent: true,
      error: null,
      trips: new Map(),
      foods: new Map(),
      recipes: new Map(),
      ratings: new Map(),
      favourites: new Map(),
      community: {},
      settings: DEFAULT_SETTINGS,
      sync: EMPTY_SYNC,
      basket: EMPTY_BASKET,
    }
  }

  // ------------------------------------------------------------ subscription

  getState = (): StoreState => this.state

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /** Hear about writes that failed (and were rolled back). */
  onError(fn: (err: Error) => void): () => void {
    this.errorListeners.add(fn)
    return () => this.errorListeners.delete(fn)
  }

  private set(next: Partial<StoreState>): void {
    this.state = { ...this.state, ...next }
    for (const fn of this.listeners) fn()
  }

  /** Resolves when every write queued so far has reached the backend. */
  flush(): Promise<void> {
    return this.queue
  }

  // ------------------------------------------------------------------ load

  load(): Promise<void> {
    if (!this.loading) this.loading = this.doLoad()
    return this.loading
  }

  private async doLoad(): Promise<void> {
    try {
      const b = this.backend
      const [trips, foods, recipes, ratings, favourites, settings, sync, basket, community] =
        await Promise.all([
          b.getAll<Trip>('trips'),
          b.getAll<Food>('foods'),
          b.getAll<Recipe>('recipes'),
          b.getAll<UserRating>('availability'),
          b.getAll<Favourite>('favourites'),
          b.get<unknown>('meta', 'settings'),
          b.get<SyncMeta>('meta', 'sync'),
          b.get<ScratchBasket>('meta', 'basket'),
          b.get<AvailabilityTable>('meta', 'community'),
        ])
      // Tombstones are kept: they stop an older copy from coming back, and hide shipped foods.
      const byId = <T extends { id: string }>(rows: T[], ok: (v: unknown) => boolean) =>
        new Map(rows.filter((r) => ok(r) || isTombstone(r)).map((r) => [r.id, r]))
      const tripRows = trips
        .map((t) =>
          isTombstone(t)
            ? normaliseTrip({ name: '', sections: [], ...(t as object) })
            : normaliseTrip(t),
        )
        .filter((t): t is Trip => t !== undefined)
      this.set({
        ready: true,
        persistent: b.kind === 'idb',
        trips: new Map(tripRows.map((t) => [t.id, t])),
        foods: byId(foods, STORABLE.foods),
        recipes: byId(recipes, STORABLE.recipes),
        ratings: byId(ratings, isRatingRecord),
        favourites: byId(favourites, isFavouriteRecord),
        settings: normaliseSettings(settings),
        sync: { ...EMPTY_SYNC, ...(isObj(sync) ? sync : {}) },
        basket: isObj(basket) && Array.isArray(basket.entries) ? basket : EMPTY_BASKET,
        community: isObj(community) ? community : {},
      })
      void this.requestPersistence()
    } catch (err) {
      this.set({ ready: true, error: err instanceof Error ? err : new Error(String(err)) })
    }
  }

  /**
   * Ask once, ever, that the browser keep this origin's storage (Safari drops a tab's
   * storage after seven days without a visit; Chrome evicts under pressure). Never throws.
   */
  private async requestPersistence(): Promise<void> {
    if (!this.persistStorage || this.backend.kind !== 'idb') return
    try {
      if (await this.backend.get<boolean>('meta', META_PERSIST_ASKED)) return
      await this.backend.put('meta', META_PERSIST_ASKED, true)
      await this.persistStorage()
    } catch (err) {
      console.warn('[resupport] persistent storage', err)
    }
  }

  // --------------------------------------------------------------- derived

  private baseCatalog: Catalog | null = null
  private catalogMemo: { inputs: unknown[]; value: Catalog } | null = null

  /**
   * Bundled data overlaid by user records (core `mergeCatalog`: newest `updated_at` wins per
   * id, soft deletes remove, availability user > community > curated per cell), then the
   * person's per-food ratings. Same object until an input changes.
   */
  catalog(): Catalog {
    const s = this.state
    const inputs = [s.foods, s.recipes, s.ratings, s.community]
    const memo = this.catalogMemo
    if (memo && memo.inputs.every((v, i) => v === inputs[i])) return memo.value
    this.baseCatalog ??= buildCatalog(this.bundled)
    const merged = mergeCatalog(this.baseCatalog, {
      foods: [...s.foods.values()],
      recipes: [...s.recipes.values()],
      availability: layerTables(communityTable(s.community), userTable(s.ratings.values())),
    })
    const value: Catalog = { ...merged, foods: applyFoodRatings(merged.foods, s.ratings.values()) }
    this.catalogMemo = { inputs, value }
    return value
  }

  private settingsMemo: { inputs: unknown[]; value: ResolvedSettings } | null = null

  resolvedSettings(): ResolvedSettings {
    const regions = this.catalog().regions
    const inputs = [this.state.settings, regions]
    const memo = this.settingsMemo
    if (memo && memo.inputs.every((v, i) => v === inputs[i])) return memo.value
    const value = resolveSettings(this.state.settings, regions, this.localeRegion)
    this.settingsMemo = { inputs, value }
    return value
  }

  // ------------------------------------------------------------ write core

  /**
   * A strictly increasing ISO timestamp, so edits in the same millisecond still order. Pass the
   * stamps of the records an edit replaces: the result is later than each of them, so an edit
   * never sorts older than what it replaces (a record pulled from a device whose clock runs
   * fast, say) and lose to it under last-writer-wins.
   */
  stamp(...replaces: (string | undefined)[]): string {
    let t = timeOf(this.clock())
    for (const prev of replaces) {
      const p = prev ? timeOf(prev) : 0
      if (p >= t) t = p + 1
    }
    if (t <= this.lastStamp) t = this.lastStamp + 1
    this.lastStamp = t
    return new Date(t).toISOString()
  }

  private read(op: Op): unknown {
    if (op.table === 'meta') return this.state[op.key]
    return this.state[FIELD[op.table]].get(op.key)
  }

  private patchFor(ops: { op: Op; value: unknown }[]): Partial<StoreState> {
    const next: Record<string, unknown> = {}
    for (const { op, value } of ops) {
      if (op.table === 'meta') {
        next[op.key] = value
        continue
      }
      const field = FIELD[op.table]
      const map = new Map((next[field] as Map<string, unknown>) ?? this.state[field])
      if (value === undefined) map.delete(op.key)
      else map.set(op.key, value)
      next[field] = map
    }
    return next as Partial<StoreState>
  }

  /**
   * Apply ops to the snapshot now; persist them in order; on failure put back what they
   * replaced, unless something newer replaced it first.
   */
  private commit(ops: Op[], opts: { markDirty?: boolean } = {}): Promise<void> {
    if (opts.markDirty !== false) {
      const dirty = { ...this.state.sync.dirty }
      let changed = false
      for (const op of ops) {
        if (op.table === 'meta') continue
        dirty[`${KIND[op.table]}:${op.key}`] = op.value.updated_at ?? ''
        changed = true
      }
      if (changed)
        ops = [...ops, { table: 'meta', key: 'sync', value: { ...this.state.sync, dirty } }]
    }
    const before = ops.map((op) => ({ op, value: this.read(op) }))
    this.set(this.patchFor(ops.map((op) => ({ op, value: op.value }))))

    const write = async () => {
      for (const op of ops) await this.backend.put(op.table as Table, op.key, op.value)
    }
    this.queue = this.queue.then(write).catch((err: unknown) => {
      const stillOurs = before.filter(({ op }) => this.read(op) === op.value)
      if (stillOurs.length > 0) this.set(this.patchFor(stillOurs))
      const error = err instanceof Error ? err : new Error(String(err))
      for (const fn of this.errorListeners) fn(error)
    })
    return this.queue
  }

  private putRecord<T extends { id: string; updated_at?: string }>(
    table: RecordTable,
    value: T,
  ): Promise<void> {
    return this.commit([{ table, key: value.id, value }])
  }

  private putMeta(key: MetaKey, value: unknown): Promise<void> {
    return this.commit([{ table: 'meta', key, value }], { markDirty: false })
  }

  // ----------------------------------------------------------------- trips

  async createTrip(input: Partial<Omit<Trip, 'id'>> & { name: string }): Promise<Trip> {
    const base = slugify(input.name) || 'trip'
    const region = input.region ?? this.resolvedSettings().region
    const trip: Trip = {
      sections: [],
      ...input,
      ...(region ? { region } : {}),
      id: newId(base.slice(0, 32)),
      updated_at: this.stamp(),
    }
    await this.putRecord('trips', trip)
    return trip
  }

  /** Patch or rewrite a trip. Returns the new trip, or undefined when there is none. */
  async updateTrip(
    id: string,
    change: Partial<Trip> | ((t: Trip) => Trip),
  ): Promise<Trip | undefined> {
    const trip = this.state.trips.get(id)
    if (!trip) return undefined
    const changed = typeof change === 'function' ? change(trip) : { ...trip, ...change }
    const next = restampTrip(trip, { ...changed, id }, this.stamp(trip.updated_at))
    await this.putRecord('trips', next)
    return next
  }

  async deleteTrip(id: string): Promise<Undo> {
    const trip = this.state.trips.get(id)
    if (!trip) return async () => undefined
    await this.putRecord('trips', {
      ...trip,
      deleted: true,
      updated_at: this.stamp(trip.updated_at),
    })
    return async () => {
      const { deleted: _gone, ...rest } = trip
      const now = this.state.trips.get(id)?.updated_at
      await this.putRecord('trips', { ...rest, updated_at: this.stamp(now) })
    }
  }

  // -------------------------------------------------------------- sections

  async createSection(tripId: string, input: NewSection): Promise<Section | undefined> {
    const trip = this.state.trips.get(tripId)
    if (!trip) return undefined
    const stamp = this.stamp(trip.updated_at)
    const region = input.region ?? trip.region
    const section: Section = {
      days: 5,
      method: 'buy-in-town',
      kcal_per_day: 3500,
      // Rule 4: slot counts are stated, never derived from days. The form fills them in.
      slots: { breakfast: 0, lunch: 0, dinner: 0 },
      day_lines: [],
      meal_lines: [],
      status: 'planned',
      basket: [],
      ...input,
      ...(region ? { region } : {}),
      id: uniqueId(
        trip.sections.map((s) => s.id),
        input.name,
      ),
      updated_at: stamp,
    }
    await this.putRecord(
      'trips',
      restampTrip(trip, { ...trip, sections: [...trip.sections, section] }, stamp),
    )
    return section
  }

  async updateSection(
    tripId: string,
    sectionId: string,
    change: Partial<Section> | ((s: Section) => Section),
  ): Promise<Section | undefined> {
    const trip = this.state.trips.get(tripId)
    const section = trip?.sections.find((s) => s.id === sectionId)
    if (!trip || !section) return undefined
    const stamp = this.stamp(trip.updated_at, section.updated_at)
    const changed = typeof change === 'function' ? change(section) : { ...section, ...change }
    const next: Section = { ...changed, id: sectionId, updated_at: stamp }
    const sections = trip.sections.map((s) => (s.id === sectionId ? next : s))
    await this.putRecord('trips', restampTrip(trip, { ...trip, sections }, stamp))
    return next
  }

  async deleteSection(tripId: string, sectionId: string): Promise<Undo> {
    const trip = this.state.trips.get(tripId)
    const index = trip?.sections.findIndex((s) => s.id === sectionId) ?? -1
    const section = trip?.sections[index]
    if (!trip || !section) return async () => undefined
    await this.updateTrip(tripId, (t) => ({
      ...t,
      sections: t.sections.filter((s) => s.id !== sectionId),
    }))
    return async () => {
      await this.updateTrip(tripId, (t) => {
        if (t.sections.some((s) => s.id === sectionId)) return t
        const sections = [...t.sections]
        sections.splice(Math.min(index, sections.length), 0, section)
        return { ...t, sections }
      })
    }
  }

  /** Move a section to a new position in its trip. */
  async moveSection(tripId: string, sectionId: string, toIndex: number): Promise<void> {
    await this.updateTrip(tripId, (t) => {
      const from = t.sections.findIndex((s) => s.id === sectionId)
      if (from < 0) return t
      const sections = [...t.sections]
      const [moved] = sections.splice(from, 1)
      if (moved) sections.splice(Math.max(0, Math.min(toIndex, sections.length)), 0, moved)
      return { ...t, sections }
    })
  }

  // ---------------------------------------------------------------- basket

  basketOf(target: BasketTarget): BasketEntry[] {
    if (target === 'scratch') return this.state.basket.entries
    return (
      this.state.trips.get(target.tripId)?.sections.find((s) => s.id === target.sectionId)
        ?.basket ?? []
    )
  }

  /** Rewrite a basket. The scratch basket is local only; a section's syncs with its trip. */
  async updateBasket(
    target: BasketTarget,
    fn: (entries: BasketEntry[]) => BasketEntry[],
  ): Promise<void> {
    if (target === 'scratch') {
      const b = this.state.basket
      await this.putMeta('basket', { ...b, entries: fn(b.entries), updated_at: this.stamp() })
      return
    }
    await this.updateSection(target.tripId, target.sectionId, (s) => ({
      ...s,
      basket: fn(s.basket),
    }))
  }

  async addBasketEntry(target: BasketTarget, entry: BasketEntry): Promise<Undo> {
    await this.updateBasket(target, (entries) => [...entries, entry])
    return () => this.updateBasket(target, (entries) => entries.filter((e) => e.id !== entry.id))
  }

  async removeBasketEntry(target: BasketTarget, entryId: string): Promise<Undo> {
    const entries = this.basketOf(target)
    const index = entries.findIndex((e) => e.id === entryId)
    const entry = entries[index]
    if (!entry) return async () => undefined
    await this.updateBasket(target, (list) => list.filter((e) => e.id !== entryId))
    return () =>
      this.updateBasket(target, (list) => {
        if (list.some((e) => e.id === entryId)) return list
        const next = [...list]
        next.splice(Math.min(index, next.length), 0, entry)
        return next
      })
  }

  /**
   * Settings for the scratch basket other than its entries (goal, region). Setting `days` or
   * `kcal_per_day` recomputes `kcal_goal` = days × kcal a day, so the three stay consistent.
   */
  async updateScratch(patch: Partial<Omit<ScratchBasket, 'entries'>>): Promise<void> {
    const next: ScratchBasket = { ...this.state.basket, ...patch, updated_at: this.stamp() }
    if (patch.days !== undefined || patch.kcal_per_day !== undefined) {
      const derived = scratchGoalOf(this.state.basket)
      next.days = Math.max(0, patch.days ?? derived.days)
      next.kcal_per_day = Math.max(0, patch.kcal_per_day ?? derived.kcal_per_day)
      next.kcal_goal = Math.round(next.days * next.kcal_per_day)
    }
    await this.putMeta('basket', next)
  }

  // -------------------------------------------------------- foods, recipes

  /**
   * Add or edit a food. Editing a shipped food makes a user copy that overlays it. The
   * person's own per-food ratings (source 'user', laid on by `catalog()`) are stripped: they
   * live as ratings, not inside the food.
   */
  async saveFood(food: Food): Promise<Food> {
    const next: Food = {
      ...stripUserAvailability(food),
      updated_at: this.stamp(food.updated_at, this.state.foods.get(food.id)?.updated_at),
    }
    delete next.deleted
    delete next.reset
    await this.putRecord('foods', next)
    return next
  }

  async deleteFood(id: string): Promise<Undo> {
    return this.softDelete(
      'foods',
      id,
      this.liveUserCopy(this.state.foods, id) ?? this.bundled.foods.find((f) => f.id === id),
    )
  }

  /**
   * Drop the person's copy (edit or hide) of a shipped food, so the shipped one shows again
   * and later data updates reach it. Writes a reset marker so the reset syncs. Undo puts the
   * earlier copy back. Does nothing for a food that did not ship or has no user copy.
   */
  async resetFood(id: string): Promise<Undo> {
    return this.reset('foods', id, this.bundled.foods)
  }

  async saveRecipe(recipe: Recipe): Promise<Recipe> {
    const next: Recipe = {
      ...recipe,
      updated_at: this.stamp(recipe.updated_at, this.state.recipes.get(recipe.id)?.updated_at),
    }
    delete next.deleted
    delete next.reset
    await this.putRecord('recipes', next)
    return next
  }

  async deleteRecipe(id: string): Promise<Undo> {
    return this.softDelete(
      'recipes',
      id,
      this.liveUserCopy(this.state.recipes, id) ?? this.bundled.recipes.find((r) => r.id === id),
    )
  }

  /** As `resetFood`, for a shipped recipe. */
  async resetRecipe(id: string): Promise<Undo> {
    return this.reset('recipes', id, this.bundled.recipes)
  }

  /** The user-layer record unless it is a reset marker (a tombstone counts). */
  private liveUserCopy<T extends { reset?: boolean }>(
    layer: ReadonlyMap<string, T>,
    id: string,
  ): T | undefined {
    const v = layer.get(id)
    return v && !v.reset ? v : undefined
  }

  private async reset(
    table: 'foods' | 'recipes',
    id: string,
    shipped: readonly { id: string }[],
  ): Promise<Undo> {
    const before = this.state[table].get(id)
    if (!before || isResetMarker(before) || !shipped.some((r) => r.id === id)) {
      return async () => undefined
    }
    await this.putRecord(table, { id, reset: true, updated_at: this.stamp(before.updated_at) })
    return async () => {
      const now = this.state[table].get(id)?.updated_at
      await this.putRecord(table, { ...before, updated_at: this.stamp(now) })
    }
  }

  private async softDelete<T extends { id: string; updated_at?: string; deleted?: boolean }>(
    table: 'foods' | 'recipes' | 'availability',
    id: string,
    current: T | undefined,
  ): Promise<Undo> {
    if (!current) return async () => undefined
    const latest = () => this.state[FIELD[table]].get(id)?.updated_at
    await this.putRecord(table, {
      ...current,
      deleted: true,
      updated_at: this.stamp(current.updated_at, latest()),
    })
    return async () => {
      const { deleted: _gone, ...rest } = current
      await this.putRecord(table, { ...rest, updated_at: this.stamp(latest()) })
    }
  }

  // ---------------------------------------------------------- availability

  /** Set this device's own rating for a category (or a food) in a region. */
  async setRating(input: Omit<AvailabilityVote, 'updated_at'>): Promise<UserRating> {
    const id = ratingKey(input.category, input.food, input.region)
    const rating: UserRating = {
      ...input,
      id,
      updated_at: this.stamp(this.state.ratings.get(id)?.updated_at),
    }
    await this.putRecord('availability', rating)
    return rating
  }

  async clearRating(id: string): Promise<Undo> {
    const current = this.state.ratings.get(id)
    if (!current || current.deleted) return async () => undefined
    return this.softDelete('availability', id, current)
  }

  /** Replace one region's community ratings (from GET /v1/availability). */
  async setCommunity(
    region: RegionCode,
    ratings: Record<string, AvailabilityRating>,
  ): Promise<void> {
    const next: AvailabilityTable = {}
    for (const [cat, byRegion] of Object.entries(this.state.community)) {
      const { [region]: _old, ...others } = byRegion
      if (Object.keys(others).length > 0) next[cat] = others
    }
    for (const [cat, rating] of Object.entries(ratings)) {
      next[cat] = { ...(next[cat] ?? {}), [region]: { ...rating, source: 'community' } }
    }
    await this.putMeta('community', next)
  }

  // ------------------------------------------------------------ favourites

  /** Is this food or recipe starred (and not un-starred since)? */
  isFavourite(target: Favourite['target'], ref: string): boolean {
    const f = this.state.favourites.get(favouriteKey(target, ref))
    return f !== undefined && !f.deleted
  }

  /**
   * Star or un-star a food or recipe. A star is its own synced record (never a copy of the
   * food), so starring a shipped food does not freeze it against data updates.
   */
  async setFavourite(target: Favourite['target'], ref: string, on: boolean): Promise<Undo> {
    const id = favouriteKey(target, ref)
    const was = this.isFavourite(target, ref)
    if (was === on) return async () => undefined
    const next: Favourite = {
      id,
      target,
      ref,
      updated_at: this.stamp(this.state.favourites.get(id)?.updated_at),
    }
    if (!on) next.deleted = true
    await this.putRecord('favourites', next)
    return () => this.setFavourite(target, ref, was).then(() => undefined)
  }

  toggleFavourite(target: Favourite['target'], ref: string): Promise<Undo> {
    return this.setFavourite(target, ref, !this.isFavourite(target, ref))
  }

  // -------------------------------------------------------------- settings

  async updateSettings(
    patch: Partial<Omit<Settings, 'sync'>> & { sync?: Partial<SyncConsent> },
  ): Promise<Settings> {
    const s = this.state.settings
    const next = normaliseSettings({
      ...s,
      ...patch,
      sync: { ...s.sync, ...(patch.sync ?? {}) },
      updated_at: this.stamp(s.updated_at),
    })
    await this.putMeta('settings', next)
    return next
  }

  // ------------------------------------------------------------------ sync

  async updateSyncMeta(change: Partial<SyncMeta> | ((m: SyncMeta) => SyncMeta)): Promise<void> {
    const m = this.state.sync
    await this.putMeta('sync', typeof change === 'function' ? change(m) : { ...m, ...change })
  }

  private toSyncRecord(
    table: RecordTable,
    value: { id: string; updated_at?: string; deleted?: boolean },
  ): SyncRecord {
    return {
      kind: KIND[table],
      id: value.id,
      updated_at: value.updated_at ?? '',
      ...(value.deleted ? { deleted: true } : {}),
      data: value,
    }
  }

  /** What a push should send: everything dirty, or (`all`) every user record. */
  pendingRecords(all = false): SyncRecord[] {
    const out: SyncRecord[] = []
    const tables: RecordTable[] = ['trips', 'foods', 'recipes', 'availability', 'favourites']
    if (all) {
      for (const t of tables) {
        for (const v of this.state[FIELD[t]].values()) out.push(this.toSyncRecord(t, v))
      }
      return out
    }
    for (const key of Object.keys(this.state.sync.dirty)) {
      const sep = key.indexOf(':')
      const table = TABLE_OF[key.slice(0, sep) as SyncKind]
      if (!table) continue
      const value = this.state[FIELD[table]].get(key.slice(sep + 1))
      if (value) out.push(this.toSyncRecord(table, value))
    }
    return out
  }

  /** After a push: forget dirty marks the server now has, unless the record changed since. */
  async markSynced(pushed: SyncRecord[], patch: Partial<SyncMeta> = {}): Promise<void> {
    await this.updateSyncMeta((m) => {
      const dirty = { ...m.dirty }
      for (const r of pushed) {
        const key = `${r.kind}:${r.id}`
        const mark = dirty[key]
        if (mark !== undefined && timeOf(mark) <= timeOf(r.updated_at)) delete dirty[key]
      }
      return { ...m, ...patch, dirty }
    })
  }

  /**
   * The end of one push: forget the dirty marks of what the server took (as `markSynced`),
   * and mark what it did not take (failed, rejected) so the next round sends it again, even
   * when it went out as part of a first full sync and was never dirty.
   */
  async settlePush(synced: SyncRecord[], retry: SyncRecord[]): Promise<void> {
    if (synced.length === 0 && retry.length === 0) return
    await this.updateSyncMeta((m) => {
      const dirty = { ...m.dirty }
      for (const r of synced) {
        const key = `${r.kind}:${r.id}`
        const mark = dirty[key]
        if (mark !== undefined && timeOf(mark) <= timeOf(r.updated_at)) delete dirty[key]
      }
      for (const r of retry) dirty[`${r.kind}:${r.id}`] ??= r.updated_at
      return { ...m, dirty }
    })
  }

  /**
   * Records from another device: last-writer-wins by `updated_at`, no stamping, no dirty
   * mark. Returns how many were newer than what is here and so applied.
   */
  applyRemote(records: SyncRecord[]): Promise<number> {
    return this.applyRecords(records.map((record) => ({ record })))
  }

  /**
   * The server's copies of records this device just pushed (`results[i].record`: the push
   * was stale, or its future stamp was pulled back to the server clock). The server's copy
   * replaces ours outright, even when ours carries the later (future) stamp, unless the
   * record was edited here again since the push; then last-writer-wins decides.
   */
  applyServerCopies(pairs: { sent: SyncRecord; record: SyncRecord }[]): Promise<number> {
    return this.applyRecords(pairs.map(({ sent, record }) => ({ record, force: sent.updated_at })))
  }

  /** `force`: replace the local record without LWW while its stamp is still this. */
  private async applyRecords(items: { record: SyncRecord; force?: string }[]): Promise<number> {
    const ops: Op[] = []
    const won: string[] = []
    const pending = new Map<string, unknown>()
    /** Merged trips holding edits the incoming copy lacked: key -> their stamp. */
    const unsent = new Map<string, string | undefined>()
    for (const { record: r, force } of items) {
      const table = TABLE_OF[r.kind]
      if (!table || !isObj(r.data) || typeof r.id !== 'string') continue
      const key = `${r.kind}:${r.id}`
      const local = (pending.get(key) ?? this.state[FIELD[table]].get(r.id)) as
        | { id: string; updated_at?: string; deleted?: boolean }
        | undefined
      let value: { id: string; updated_at?: string; deleted?: boolean } | undefined
      if (r.deleted) {
        // A tombstone carries only `{ id }`. Keep what is known of the record under it, so a
        // deleted trip still reads as a trip (undo, screens that look it up by id).
        value = { ...(local && !isResetMarker(local) ? local : {}), id: r.id }
        if (table === 'trips') value = normaliseTrip({ name: '', sections: [], ...value })
        if (!value) continue
        value = { ...value, updated_at: r.updated_at, deleted: true }
      } else {
        const raw = { ...r.data, id: r.id, updated_at: r.updated_at }
        value = table === 'trips' ? normaliseTrip(raw) : STORABLE[table](raw) ? raw : undefined
        if (!value) continue
      }
      const replace = force !== undefined && local !== undefined && local.updated_at === force
      if (
        table === 'trips' &&
        !replace &&
        !r.deleted &&
        local &&
        !local.deleted &&
        isTripLike(local)
      ) {
        // Two live copies of a trip merge section by section, as on the worker, so an edit
        // made here to one section survives another device's edit to a different one.
        const incoming = value as Trip
        const merged = normaliseTrip(mergeTrips(local as Trip, incoming))
        if (!merged || canonicalJson(merged) === canonicalJson(local)) continue
        ops.push({ table, key: r.id, value: merged })
        pending.set(key, merged)
        // Ours held something theirs lacked: keep it dirty so the merge goes back up.
        if (canonicalJson(merged) !== canonicalJson(incoming)) unsent.set(key, merged.updated_at)
        else unsent.delete(key)
        if (!won.includes(key)) won.push(key)
        continue
      }
      if (local && !replace && !isNewer(value, local)) continue
      ops.push({ table, key: r.id, value })
      pending.set(key, value)
      unsent.delete(key)
      if (!won.includes(key)) won.push(key)
    }
    if (ops.length === 0) return 0
    const dirty = { ...this.state.sync.dirty }
    for (const k of won) {
      if (unsent.has(k)) dirty[k] = unsent.get(k) ?? ''
      else delete dirty[k]
    }
    ops.push({ table: 'meta', key: 'sync', value: { ...this.state.sync, dirty } })
    await this.commit(ops, { markDirty: false })
    return won.length
  }

  // --------------------------------------------------------- export/import

  exportData(): ExportFile {
    const live = <T extends { deleted?: boolean }>(m: ReadonlyMap<string, T>) =>
      [...m.values()].filter((v) => !v.deleted && !isResetMarker(v))
    const s = this.state
    return {
      format: 'resupport-export',
      version: 1,
      exported_at: this.clock(),
      trips: live(s.trips),
      foods: live(s.foods),
      recipes: live(s.recipes),
      ratings: live(s.ratings),
      favourites: live(s.favourites),
      settings: s.settings,
      basket: s.basket,
    }
  }

  /**
   * Merge an export file in. Per record, newer wins (a record with no timestamp only fills a
   * gap). Sync consent is never imported: a file cannot switch uploading on.
   */
  async importData(raw: unknown): Promise<ImportSummary> {
    if (!isObj(raw) || raw.format !== 'resupport-export') {
      throw new Error('This is not a Resupport export file.')
    }
    const summary: ImportSummary = { trips: 0, foods: 0, recipes: 0, ratings: 0, skipped: 0 }
    const ops: Op[] = []
    const take = (table: RecordTable, list: unknown, count: keyof ImportSummary) => {
      if (!Array.isArray(list)) return
      if (count === 'favourites' && list.length > 0) summary.favourites ??= 0
      for (const item of list) {
        if (!GUARD[table](item)) {
          summary.skipped++
          continue
        }
        const rec = (table === 'trips' ? normaliseTrip(item) : item) as {
          id: string
          updated_at?: string
        }
        const local = this.state[FIELD[table]].get(rec.id)
        if (local && !isNewer(rec, local)) {
          summary.skipped++
          continue
        }
        ops.push({
          table,
          key: rec.id,
          value: rec.updated_at ? rec : { ...rec, updated_at: this.stamp() },
        })
        summary[count] = (summary[count] ?? 0) + 1
      }
    }
    take('trips', raw.trips, 'trips')
    take('foods', raw.foods, 'foods')
    take('recipes', raw.recipes, 'recipes')
    take('availability', raw.ratings, 'ratings')
    take('favourites', raw.favourites, 'favourites')
    if (ops.length > 0) await this.commit(ops)
    if (isObj(raw.settings)) {
      const incoming = normaliseSettings(raw.settings)
      if (isNewer(incoming, this.state.settings)) {
        await this.putMeta('settings', { ...incoming, sync: this.state.settings.sync })
      }
    }
    if (
      isObj(raw.basket) &&
      Array.isArray(raw.basket.entries) &&
      this.state.basket.entries.length === 0
    ) {
      await this.putMeta('basket', raw.basket)
    }
    return summary
  }
}
